import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useBrand } from "../providers/BootstrapProvider";
import LegalFooter from "../components/LegalFooter";

/**
 * CMSLegalPage — Phase 71.
 *
 * One component powers /privacy, /terms, /cookie-policy, /disclaimer by
 * reading `slug` from the URL config and fetching the live founder-edited
 * HTML + title + last-updated timestamp from the CMS. The old
 * PrivacyPolicy.jsx / TermsOfService.jsx hardcoded JSX is retired by
 * pointing the App.js routes to this component.
 */

const SLUG_MAP = {
  "/privacy": "privacy",
  "/terms": "terms",
  "/cookie-policy": "cookie",
  "/disclaimer": "disclaimer",
};

const CMSLegalPage = ({ slug: slugProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const brand = useBrand();
  const slug = slugProp || params.slug || "privacy";

  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null);
    setError(null);
    axios
      .get(`${API}/legal/${slug}/public`)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load page"));
  }, [slug]);

  const title = doc?.title || "Legal";
  const updatedLabel = doc?.updated_at
    ? new Date(doc.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid={`legal-page-${slug}`}>
      <nav className="border-b border-[#E5DFEC] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={brand.logo_url} alt="DHDH" className="w-10 h-10 rounded-full object-cover" />
            <span className="font-medium text-[#6B4E8A]">Dental Hygiene Development Hub</span>
          </a>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#6B4E8A]"
            data-testid="legal-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0] mb-3">Legal</p>
        <h1
          className="text-4xl sm:text-5xl font-light text-[#6B4E8A] mb-3"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="legal-title"
        >
          {title}
        </h1>
        {updatedLabel && (
          <p className="text-sm text-[#64748B] mb-10" data-testid="legal-updated">
            Last updated: {updatedLabel}
          </p>
        )}
        {!updatedLabel && doc && (
          <p className="text-sm text-[#64748B] mb-10">Not yet published</p>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {doc && (
          <div
            className="prose prose-slate max-w-none [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:text-[#6B4E8A] [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-[#6B4E8A] [&_p]:text-[#475569] [&_p]:leading-relaxed [&_p]:my-3 [&_li]:text-[#475569] [&_li]:my-1 [&_a]:text-[#8DB5A0] [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.html || "") }}
            data-testid="legal-body"
          />
        )}
      </article>

      <LegalFooter />
    </div>
  );
};

export { SLUG_MAP };
export default CMSLegalPage;
